import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Card, Form, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { askQuestion } from '../features/chatSlice';
import EmailPrompt from '../components/EmailPrompt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import '../styles/ai-theme.css';

const useStreamingEffect = (text, onUpdate, cps = 60) => {
  const idx = useRef(0);
  const last = useRef(0);
  const rate = cps / 1000;
  useEffect(() => {
    if (!text) return;
    idx.current = 0;
    last.current = 0;
    let raf;
    const tick = (ts) => {
      if (!last.current) last.current = ts;
      const delta = ts - last.current;
      last.current = ts;
      const step = Math.max(1, Math.floor(delta * rate * 8));
      idx.current = Math.min(text.length, idx.current + step);
      onUpdate(text.slice(0, idx.current));
      if (idx.current < text.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, onUpdate, rate]);
};

const useAutoResize = (ref, max = 160) => {
  const resize = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = `${Math.min(ref.current.scrollHeight, max)}px`;
  }, [ref, max]);
  return resize;
};

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); return true; } catch { return false; }
};

const Avatar = React.memo(function Avatar({ type }) {
  const isBot = type === 'bot';
  const base = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    flex: '0 0 40px',
    userSelect: 'none',
    transform: 'translateZ(0)',
    transition: 'transform .2s ease',
  };
  const style = {
    ...base,
    background: isBot
      ? 'linear-gradient(135deg,#0ea5e9 0%,#3b82f6 100%)'
      : 'linear-gradient(135deg,#64748b 0%,#0f172a 100%)',
  };
  const title = isBot ? 'AI Agent' : 'You';

  return (
    <div className={`avatar ${isBot ? 'avatar-bot' : 'avatar-user'}`} style={style} title={title} aria-label={title}>
      {isBot ? (
        <svg width="22" height="22" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path fill="white" d="M11 2h2v2.1a7.96 7.96 0 0 1 6 7.9v5a3 3 0 0 1-3 3h-8a3 3 0 0 1-3-3v-5A7.96 7.96 0 0 1 11 4.1V2Zm-4 10h2v2H7v-2Zm8 0h2v2h-2v-2ZM9 18h6a1 1 0 0 0 1-1v-1H8v1a1 1 0 0 0 1 1Z"/>
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path fill="white" d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z"/>
        </svg>
      )}
    </div>
  );
});

const MessageItem = React.memo(function MessageItem({ msg, isLast }) {
  const [stream, setStream] = useState('');
  const [copied, setCopied] = useState(false);
  const plugins = useMemo(() => [remarkGfm], []);
  const rehype = useMemo(() => (msg.sender === 'bot' ? [rehypeHighlight] : []), [msg.sender]);
  const showStream = isLast && msg.sender === 'bot' && msg.fullText;
  useStreamingEffect(showStream ? msg.fullText : '', setStream, 60);

  const raw = showStream ? stream : (msg.fullText || msg.text || '');
  const isUser = msg.sender === 'user';

  const onCopy = async () => {
    const ok = await copyText(typeof raw === 'string' ? raw : String(raw));
    setCopied(ok);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className={`d-flex align-items-start gap-2 mb-3 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
      {!isUser && <Avatar type="bot" />}
      <div className="msg-wrap position-relative">
        <div className={`msg ${isUser ? 'msg-user' : 'msg-bot'}`}>
          <ReactMarkdown children={raw} remarkPlugins={plugins} rehypePlugins={rehype} />
        </div>
        <button
          type="button"
          aria-label="Copy message"
          className={`copy-btn ${copied ? 'copied' : ''}`}
          onClick={onCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {isUser && <Avatar type="user" />}
    </div>
  );
});

/* ---- NEW: Suggestion bar shown when conversation is empty ---- */
const SuggestionBar = React.memo(function SuggestionBar({ onPick }) {
  const items = [
    { label: 'I want to buy …', fill: 'I want to buy ' },
    { label: 'How do I track my order?', fill: 'How do I track my order?' },
    { label: 'I want to buy chandelier', fill: 'I want to buy chandelier' },
    { label: 'i want to buy game accessories', fill: 'i want to buy game accessories' },
  ];
  return (
    <div className="sugg-bar" role="list" aria-label="Suggested prompts">
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          role="listitem"
          className="sugg-chip"
          onClick={() => onPick(it.fill)}
          title={it.label}
          aria-label={it.label}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
});

function ChatPage() {
  const dispatch = useDispatch();
  const { history, email, guest_name, loading } = useSelector((s) => s.chat);
  const [question, setQuestion] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const composerRef = useRef(null);

  const autoResize = useAutoResize(inputRef, 160);
  const [composerH, setComposerH] = useState(72);

  useEffect(() => { if (!email) setShowPrompt(true); }, [email]);

  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = Math.ceil(entries[0].contentRect.height);
      setComposerH(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [history.length]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const toBottom = () => { el.scrollTop = el.scrollHeight; };

    const mo = new MutationObserver(() => requestAnimationFrame(toBottom));
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    const ro = new ResizeObserver(() => requestAnimationFrame(toBottom));
    ro.observe(el);

    window.addEventListener('resize', toBottom);
    return () => {
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', toBottom);
    };
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(gap > 120);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    dispatch(askQuestion(q));
    setQuestion('');
    inputRef.current?.focus();
  }, [dispatch, question]);

  const onKeyDown = (e) => {
    const metaEnter = (e.ctrlKey || e.metaKey) && e.key === 'Enter';
    const plainEnter = e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey;
    if (metaEnter || plainEnter) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const onPickSuggestion = (text) => {
    setQuestion(text);
    inputRef.current?.focus();
  };

  return (
    <div className="ai-scope">
      <Helmet>
        <title>Chat with MazesAI — Smart AI Agent</title>
        <meta
          name="description"
          content="Chat directly with MazesAI — the AI agent that handles our brand’s activities across every platform, helps customers, and simplifies our user experience."
        />
        <meta
          name="keywords"
          content="MazesAI chat, AI assistant, conversational AI, support bot, business chatbot, sales AI, customer interaction"
        />
        <meta property="og:title" content="Chat with MazesAI — Smart Conversational AI Agent" />
        <meta
          property="og:description"
          content="Interact with MazesAI to experience how it guides, sells, and supports — all in our brand’s voice."
        />
        <meta property="og:url" content="https://mazesai.com/chat" />
        <meta property="og:image" content="/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Chat with MazesAI — AI Agent" />
        <meta
          name="twitter:description"
          content="Ask questions, get help, and explore — MazesAI makes conversations natural and helpful across every platform."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>

      <div className="ai-bg">
        <Container className="page-pad" style={{ maxWidth: 960, position: 'relative', zIndex: 1 }}>
          <Card className="glass-card p-0 chat-card">
            <div className="glass-header chat-header">
              <div className="badge-row">
                <div className="badge-soft">AI Agent</div>
                <div className="badge-soft">Secure</div>
                <div className="badge-soft">Realtime</div>
              </div>
              <div className="user-chip">User: {email || guest_name || 'Guest'}</div>
            </div>

            <div className="chat-body">
              {/* NEW: Suggestions only when there is no conversation yet */}
              {history.length === 0 && !loading && (
                <SuggestionBar onPick={onPickSuggestion} />
              )}

              <div
                ref={listRef}
                className="chat-wrap"
                aria-live="polite"
                aria-busy={loading ? 'true' : 'false'}
                style={{ '--composer-h': `${composerH}px` }}
              >
                {history.map((msg, i) => (
                  <MessageItem key={i} msg={msg} isLast={i === history.length - 1} />
                ))}
                {loading && (
                  <div className="d-flex mb-3 justify-content-start">
                    <div className="msg msg-bot typing">
                      Typing<span className="blink" />
                    </div>
                  </div>
                )}
              </div>

              <Form
                ref={composerRef}
                onSubmit={onSubmit}
                className="composer-sticky d-flex flex-column flex-sm-row align-items-stretch gap-2"
              >
                <Form.Control
                  ref={inputRef}
                  className="input-ai chat-input"
                  as="textarea"
                  rows={1}
                  onInput={useAutoResize(inputRef, 160)}
                  onKeyDown={onKeyDown}
                  placeholder="Try: “I want to buy home accessories"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <Button
                  type="submit"
                  className="btn-ai send-btn"
                  disabled={loading || !question.trim()}
                >
                  Send
                </Button>
              </Form>
            </div>
          </Card>

          {showScrollBtn && (
            <button
              type="button"
              className="scroll-bottom-btn"
              aria-label="Scroll to latest"
              onClick={scrollToBottom}
              title="Jump to latest"
            >
              ↓
            </button>
          )}
        </Container>

        <EmailPrompt show={showPrompt} onClose={() => setShowPrompt(false)} />
      </div>
    </div>
  );
}

export default ChatPage;
